import {
  DeleteOutlined,
  FlagOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DollarCircleOutlined,
  ExclamationCircleOutlined,
  EyeInvisibleOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { Tag } from "antd";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

/**
 * Статус заявки или робота.
 *
 * Цвет несёт смысл: `pending` — то, что ждёт оператора, и он обязан выделяться
 * в таблице из двадцати строк. Остальные приглушены намеренно.
 *
 * Иконка дублирует цвет не для красоты: в таблице заявок соседние строки
 * различаются одним словом, и форма значка ловится взглядом быстрее оттенка —
 * а при цветовой слепоте только она и работает.
 */
const STATUSES: Record<string, { color: string; icon: ReactNode }> = {
  pending: { color: "gold", icon: <ClockCircleOutlined aria-hidden /> },
  approved: { color: "blue", icon: <CheckCircleOutlined aria-hidden /> },
  paid: { color: "green", icon: <DollarCircleOutlined aria-hidden /> },
  rejected: { color: "red", icon: <CloseCircleOutlined aria-hidden /> },
  active: { color: "green", icon: <PlayCircleOutlined aria-hidden /> },
  // Состояния робота (ТЗ §30). `paused` — не «выключён», а «запуск закрыт,
  // история цела», поэтому знак предупреждения, а не пауза: паузой уже
  // помечен `stopped`, и два одинаковых значка рядом в таблице не читаются.
  paused: { color: "gold", icon: <ExclamationCircleOutlined aria-hidden /> },
  hidden: { color: "default", icon: <EyeInvisibleOutlined aria-hidden /> },
  stopped: { color: "default", icon: <PauseCircleOutlined aria-hidden /> },
  blocked: { color: "red", icon: <StopOutlined aria-hidden /> },
  // Статусы отзывов (доработки §6.2). `hidden` выше уже есть, и семантика та
  // же — скрыто решением оператора. `flagged` отделён от него намеренно: это
  // «на это пожаловались люди», а не «я так решил», и в очереди эти два случая
  // разбирают по-разному.
  published: { color: "green", icon: <CheckCircleOutlined aria-hidden /> },
  flagged: { color: "volcano", icon: <FlagOutlined aria-hidden /> },
  deleted: { color: "default", icon: <DeleteOutlined aria-hidden /> },
};

export function StatusTag({ status }: { status: string }) {
  const { t } = useTranslation();
  const known = STATUSES[status];

  return (
    <Tag
      color={known?.color ?? "default"}
      icon={known?.icon}
      style={{ marginInlineEnd: 0, paddingBlock: 1, paddingInline: 8 }}
    >
      {t(`status.${status}`, status)}
    </Tag>
  );
}
